import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faPen,
	faSave,
	faExclamationCircle,
	faSearch,
	faCalculator,
	faCheckCircle,
	faChartLine,
	faAward,
	faExclamationTriangle,
	faFilter,
	faSortAmountDown,
	faSortAmountUp,
	faKeyboard,
	faQuestionCircle,
	faTimes,
	faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../common/Modal";
import {
	useGetStudentsByDepartmentQuery,
	useGetGradesByAssessmentQuery,
	useCreateAssessmentGradeMutation,
	useUpdateAssessmentGradeMutation,
} from "../../../store/api";

import { getStudentDisplayName } from "../../../utils/nameUtils";

import styles from "./AssessmentGradesModal.module.css";

const AssessmentGradesModal = ({ assessment, departmentId, onClose }) => {
	// ================= REFS & DOM ACCESS =================
	const studentRowsRef = useRef({});
	const gradeDisplaysRef = useRef({});
	const inputRefs = useRef({});
	const initialLoadCompleteRef = useRef(false);
	const modalRef = useRef(null);

	// ================= DATA STATE =================
	const [studentList, setStudentList] = useState([]);
	const [gradeValues, setGradeValues] = useState({});
	const [originalGradeValues, setOriginalGradeValues] = useState({}); // Track original values
	const [gradedStudentIds, setGradedStudentIds] = useState(new Set());

	// ================= UI STATE =================
	const [searchTerm, setSearchTerm] = useState("");
	const [filterMode, setFilterMode] = useState("all");
	const [showFilterDropdown, setShowFilterDropdown] = useState(false);
	const [sortConfig, setSortConfig] = useState({
		key: "studentName",
		direction: "asc",
	});
	const [stats, setStats] = useState({
		average: 0,
		highest: 0,
		lowest: 0,
		graded: 0,
		ungraded: 0,
	});
	const [selectedRows, setSelectedRows] = useState([]);
	const [editingRow, setEditingRow] = useState(null);
	const [quickEditMode, setQuickEditMode] = useState(false);
	const [currentQuickEditIndex, setCurrentQuickEditIndex] = useState(0);
	const [showHelp, setShowHelp] = useState(false);
	const [customGradeValue, setCustomGradeValue] = useState("");
	const [savingGrades, setSavingGrades] = useState(new Set());
	const [saveErrors, setSaveErrors] = useState(new Set());

	// ================= RTK QUERY HOOKS =================
	const {
		data: studentsData = [],
		isLoading: studentsLoading,
		error: studentsError,
		isSuccess: studentsSuccess,
	} = useGetStudentsByDepartmentQuery(departmentId);

	const {
		data: gradesData = [],
		isLoading: gradesLoading,
		error: gradesError,
		isSuccess: gradesSuccess,
	} = useGetGradesByAssessmentQuery(assessment.id);

	const [createGrade, { isLoading: isCreating }] =
		useCreateAssessmentGradeMutation();
	const [updateGrade, { isLoading: isUpdating }] =
		useUpdateAssessmentGradeMutation();

	// Loading states
	const isDataLoading = studentsLoading || gradesLoading;
	const isDataReady = studentsSuccess && gradesSuccess && !isDataLoading;
	const hasError = studentsError || gradesError;
	const isSaving = isCreating || isUpdating;

	// ================= KEYBOARD EVENT HANDLERS =================
	useEffect(() => {
		const handleGlobalKeyDown = (e) => {
			if (quickEditMode) {
				// Don't interfere with input typing
				if (e.target.tagName === "INPUT") return;

				if (e.key === "Escape") {
					e.preventDefault();
					exitQuickEditMode();
				}
			} else if (e.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("keydown", handleGlobalKeyDown);
		return () => document.removeEventListener("keydown", handleGlobalKeyDown);
	}, [quickEditMode, onClose]);

	// ================= DATA PROCESSING =================
	useEffect(() => {
		if (isDataReady && !initialLoadCompleteRef.current) {
			const gradesMap = {};
			gradesData.forEach((grade) => {
				gradesMap[grade.studentId] = {
					gradeId: grade.id,
					gradeValue: grade.grade,
				};
			});

			const processedStudentList = studentsData.map((student) => ({
				studentId: student.id,
				studentName: getStudentDisplayName(student),
				gradeId: gradesMap[student.id]?.gradeId || null,
				isGraded: !!gradesMap[student.id],
			}));

			setStudentList(processedStudentList);

			const gradeMap = {};
			const gradedIds = new Set();

			studentsData.forEach((student) => {
				if (gradesMap[student.id]) {
					gradeMap[student.id] = gradesMap[student.id].gradeValue.toString();
					gradedIds.add(student.id);
				} else {
					gradeMap[student.id] = "";
				}
			});

			setGradeValues(gradeMap);
			setOriginalGradeValues(gradeMap); // Store original values
			setGradedStudentIds(gradedIds);
			calculateStats(gradeMap, gradedIds);
			initialLoadCompleteRef.current = true;
		}
	}, [isDataReady, studentsData, gradesData]);

	// Calculate statistics from the grade values
	const calculateStats = (gradeData, gradedIds) => {
		const values = Array.from(gradedIds)
			.map((studentId) => parseFloat(gradeData[studentId]))
			.filter((num) => !isNaN(num));

		const graded = values.length;
		const ungraded = Object.keys(gradeData).length - graded;

		if (values.length > 0) {
			const sum = values.reduce((a, b) => a + b, 0);
			const average = sum / values.length;
			const highest = Math.max(...values);
			const lowest = Math.min(...values);

			setStats({
				average: average.toFixed(1),
				highest: highest.toFixed(1),
				lowest: lowest.toFixed(1),
				graded,
				ungraded,
			});
		} else {
			setStats({
				average: 0,
				highest: 0,
				lowest: 0,
				graded,
				ungraded,
			});
		}
	};

	// ================= GRADE OPERATIONS =================
	const handleGradeChange = (studentId, value) => {
		if (value === "") {
			setGradeValues((prev) => ({
				...prev,
				[studentId]: "",
			}));
			return;
		}

		const validGradeRegex = /^(\d*\.?\d*)$/;
		if (!validGradeRegex.test(value)) {
			return;
		}

		const numericGrade = parseFloat(value);
		const maxPoints = parseFloat(assessment.points);

		if (numericGrade < 0 || numericGrade > maxPoints) {
			return;
		}

		setGradeValues((prev) => ({
			...prev,
			[studentId]: value,
		}));
	};

	const saveGrade = async (studentId, moveToNext = false) => {
		const gradeValue = gradeValues[studentId];
		const originalValue = originalGradeValues[studentId];

		// Check if grade actually changed
		const hasChanged = gradeValue !== originalValue;

		if (gradeValue === "" || gradeValue === null || gradeValue === undefined) {
			setEditingRow(null);
			if (moveToNext && quickEditMode) {
				moveToNextStudent();
			}
			return;
		}

		const numericGrade = parseFloat(gradeValue);
		const maxPoints = parseFloat(assessment.points);

		if (isNaN(numericGrade) || numericGrade < 0 || numericGrade > maxPoints) {
			return;
		}

		const student = studentList.find((s) => s.studentId === studentId);
		if (!student) return;

		// Only save to backend if grade actually changed
		if (hasChanged) {
			// Add to saving set
			setSavingGrades((prev) => new Set([...prev, studentId]));
			setSaveErrors((prev) => {
				const newSet = new Set(prev);
				newSet.delete(studentId);
				return newSet;
			});

			try {
				const gradeData = {
					studentId: studentId,
					subjectAssessmentId: assessment.id,
					grade: gradeValue,
					date: new Date().toISOString(),
				};

				if (student.gradeId) {
					await updateGrade({
						id: student.gradeId,
						...gradeData,
					}).unwrap();
				} else {
					const newGrade = await createGrade(gradeData).unwrap();
					setStudentList((prev) =>
						prev.map((s) =>
							s.studentId === studentId
								? { ...s, gradeId: newGrade.id, isGraded: true }
								: s
						)
					);
				}

				setGradedStudentIds((prev) => new Set([...prev, studentId]));

				// Update original value after successful save
				setOriginalGradeValues((prev) => ({
					...prev,
					[studentId]: gradeValue,
				}));

				if (gradeDisplaysRef.current[studentId]) {
					const display = gradeDisplaysRef.current[studentId];
					display.textContent = gradeValue;
					display.className = `${styles.gradeDisplay}`;
				}

				const newGradedIds = new Set([...gradedStudentIds, studentId]);
				calculateStats(
					{ ...gradeValues, [studentId]: gradeValue },
					newGradedIds
				);
			} catch (error) {
				console.error("Error saving grade:", error);
				setSaveErrors((prev) => new Set([...prev, studentId]));
				// Show user-friendly error feedback
				alert(
					`Failed to save grade for ${student.studentName}. Please try again.`
				);
			} finally {
				setSavingGrades((prev) => {
					const newSet = new Set(prev);
					newSet.delete(studentId);
					return newSet;
				});
			}
		}

		setEditingRow(null);

		if (moveToNext && quickEditMode) {
			moveToNextStudent();
		}
	};

	// ================= QUICK EDIT NAVIGATION =================
	const moveToNextStudent = () => {
		if (!quickEditMode) return;

		const visibleStudents = getVisibleStudents();
		const nextIndex = currentQuickEditIndex + 1;

		if (nextIndex < visibleStudents.length) {
			const nextStudentId = visibleStudents[nextIndex].studentId;

			// Update state synchronously
			setCurrentQuickEditIndex(nextIndex);
			setEditingRow(nextStudentId);

			// Track original value when starting to edit
			setOriginalGradeValues((prev) => ({
				...prev,
				[nextStudentId]: gradeValues[nextStudentId] || "",
			}));

			// Focus the input with a more robust approach
			setTimeout(() => {
				const input = inputRefs.current[nextStudentId];
				if (input && quickEditMode) {
					// Double check we're still in quick edit mode
					input.focus();
					input.select();
				}
			}, 50);
		}
		// If at end of list, stay on current student (don't exit edit mode)
	};

	const moveToPreviousStudent = () => {
		if (!quickEditMode) return;

		const visibleStudents = getVisibleStudents();
		const prevIndex = currentQuickEditIndex - 1;

		if (prevIndex >= 0) {
			const prevStudentId = visibleStudents[prevIndex].studentId;

			// Update state synchronously
			setCurrentQuickEditIndex(prevIndex);
			setEditingRow(prevStudentId);

			// Track original value when starting to edit
			setOriginalGradeValues((prev) => ({
				...prev,
				[prevStudentId]: gradeValues[prevStudentId] || "",
			}));

			// Focus the input with a more robust approach
			setTimeout(() => {
				const input = inputRefs.current[prevStudentId];
				if (input && quickEditMode) {
					// Double check we're still in quick edit mode
					input.focus();
					input.select();
				}
			}, 50);
		}
		// If at beginning of list, stay on current student (don't exit edit mode)
	};

	const handleKeyDown = (e, studentId) => {
		// Prevent rapid key presses from interfering
		if (savingGrades.has(studentId)) return;

		if (e.key === "Enter" || e.key === "ArrowDown") {
			e.preventDefault();
			saveGrade(studentId, true); // Save and move to next
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			saveGrade(studentId, false); // Save without auto-moving
			setTimeout(() => moveToPreviousStudent(), 50); // Small delay to prevent conflicts
		} else if (e.key === "Escape") {
			e.preventDefault();
			if (quickEditMode) {
				exitQuickEditMode();
			} else {
				setEditingRow(null);
			}
		}
	};

	const enterQuickEditMode = () => {
		setQuickEditMode(true);
		const visibleStudents = getVisibleStudents();
		if (visibleStudents.length > 0) {
			const firstStudentId = visibleStudents[0].studentId;
			setCurrentQuickEditIndex(0);
			setEditingRow(firstStudentId);

			// Track original value when starting to edit
			setOriginalGradeValues((prev) => ({
				...prev,
				[firstStudentId]: gradeValues[firstStudentId] || "",
			}));

			// Focus the input with improved timing
			setTimeout(() => {
				const input = inputRefs.current[firstStudentId];
				if (input && quickEditMode) {
					input.focus();
					input.select();
				}
			}, 50);
		}
	};

	const exitQuickEditMode = () => {
		setQuickEditMode(false);
		setEditingRow(null);
		setCurrentQuickEditIndex(0);
	};

	// ================= FILTER & SORT OPERATIONS =================
	const getVisibleStudents = () => {
		let filtered = studentList;

		if (searchTerm) {
			filtered = filtered.filter((student) =>
				student.studentName.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}

		if (filterMode === "graded") {
			filtered = filtered.filter((student) =>
				gradedStudentIds.has(student.studentId)
			);
		} else if (filterMode === "ungraded") {
			filtered = filtered.filter(
				(student) => !gradedStudentIds.has(student.studentId)
			);
		}

		filtered.sort((a, b) => {
			let aValue, bValue;

			switch (sortConfig.key) {
				case "studentName":
					aValue = a.studentName;
					bValue = b.studentName;
					break;
				case "grade":
					aValue = gradedStudentIds.has(a.studentId)
						? parseFloat(gradeValues[a.studentId])
						: -1;
					bValue = gradedStudentIds.has(b.studentId)
						? parseFloat(gradeValues[b.studentId])
						: -1;
					break;
				default:
					return 0;
			}

			if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
			if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
			return 0;
		});

		return filtered;
	};

	const requestSort = (key) => {
		let direction = "asc";
		if (sortConfig.key === key && sortConfig.direction === "asc") {
			direction = "desc";
		}
		setSortConfig({ key, direction });
	};

	// ================= BULK OPERATIONS =================
	const toggleSelectAll = () => {
		const visibleStudents = getVisibleStudents();
		const visibleIds = visibleStudents.map((s) => s.studentId);

		if (selectedRows.length === visibleIds.length) {
			setSelectedRows([]);
		} else {
			setSelectedRows(visibleIds);
		}
	};

	const applyQuickGrade = async (value) => {
		if (selectedRows.length === 0) {
			return;
		}

		let gradeValue = value;
		if (value === "100%") {
			gradeValue = assessment.points.toString();
		} else if (value === "50%") {
			gradeValue = (Number(assessment.points) * 0.5).toString();
		} else if (value === "0%" || value === "0") {
			gradeValue = "0";
		}

		// Update local state first
		selectedRows.forEach((studentId) => {
			setGradeValues((prev) => ({ ...prev, [studentId]: gradeValue }));
		});

		// Save all grades to backend
		await saveSelectedGrades(gradeValue);
	};

	const saveSelectedGrades = async (gradeValue) => {
		if (selectedRows.length === 0) return;

		const savePromises = selectedRows.map(async (studentId) => {
			// Check if grade actually changed
			const originalValue = originalGradeValues[studentId];
			const hasChanged = gradeValue !== originalValue;

			// Update local state
			setGradeValues((prev) => ({ ...prev, [studentId]: gradeValue }));

			// Only save to backend if value changed
			if (!hasChanged) return;

			const student = studentList.find((s) => s.studentId === studentId);
			if (!student) return;

			setSavingGrades((prev) => new Set([...prev, studentId]));

			try {
				const gradeData = {
					studentId: studentId,
					subjectAssessmentId: assessment.id,
					grade: gradeValue,
					date: new Date().toISOString(),
				};

				if (student.gradeId) {
					await updateGrade({
						id: student.gradeId,
						...gradeData,
					}).unwrap();
				} else {
					const newGrade = await createGrade(gradeData).unwrap();
					setStudentList((prev) =>
						prev.map((s) =>
							s.studentId === studentId
								? { ...s, gradeId: newGrade.id, isGraded: true }
								: s
						)
					);
				}

				setGradedStudentIds((prev) => new Set([...prev, studentId]));

				// Update original value after successful save
				setOriginalGradeValues((prev) => ({
					...prev,
					[studentId]: gradeValue,
				}));
			} catch (error) {
				console.error(`Error saving grade for student ${studentId}:`, error);
				setSaveErrors((prev) => new Set([...prev, studentId]));
			} finally {
				setSavingGrades((prev) => {
					const newSet = new Set(prev);
					newSet.delete(studentId);
					return newSet;
				});
			}
		});

		try {
			await Promise.all(savePromises);

			// Recalculate stats after all saves
			const newGradedIds = new Set([...gradedStudentIds, ...selectedRows]);
			const newGradeValues = { ...gradeValues };
			selectedRows.forEach((id) => {
				newGradeValues[id] = gradeValue;
			});
			calculateStats(newGradeValues, newGradedIds);

			setSelectedRows([]);
		} catch (err) {
			console.error("Error in batch save:", err);
			alert("Some grades failed to save. Please check and try again.");
		}
	};

	const visibleStudents = getVisibleStudents();

	// Custom header content
	const customHeader = (
		<div className={styles.customHeader}>
			<div className={styles.headerContent}>
				<div className={styles.titleSection}>
					<h2 className={styles.title}>
						{assessment.title}
						<span className={styles.subtitle}>
							{assessment.type} • {assessment.points} points
						</span>
					</h2>
				</div>

				<div className={styles.statsContainer}>
					<div className={styles.statItem}>
						<FontAwesomeIcon icon={faCalculator} className={styles.statIcon} />
						<div className={styles.statValue}>{stats.average}</div>
						<div className={styles.statLabel}>Average</div>
					</div>
					<div className={styles.statItem}>
						<FontAwesomeIcon icon={faAward} className={styles.statIcon} />
						<div className={styles.statValue}>{stats.highest}</div>
						<div className={styles.statLabel}>Highest</div>
					</div>
					<div className={styles.statItem}>
						<FontAwesomeIcon
							icon={faExclamationTriangle}
							className={styles.statIcon}
						/>
						<div className={styles.statValue}>{stats.lowest}</div>
						<div className={styles.statLabel}>Lowest</div>
					</div>
					<div className={styles.statItem}>
						<FontAwesomeIcon icon={faChartLine} className={styles.statIcon} />
						<div className={styles.statValue}>
							{stats.graded}/{studentList.length}
						</div>
						<div className={styles.statLabel}>Graded</div>
					</div>
				</div>

				<button className={styles.closeButton} onClick={onClose} title="Close">
					<FontAwesomeIcon icon={faTimes} />
				</button>
			</div>
		</div>
	);

	// Footer content with help tooltip
	const footer = (
		<>
			<div className={styles.helpContainer}>
				<button
					className={styles.helpButton}
					onMouseDown={() => setShowHelp(true)}
					onMouseUp={() => setShowHelp(false)}
					onMouseLeave={() => setShowHelp(false)}
					title="Hold for help"
				>
					<FontAwesomeIcon icon={faQuestionCircle} />
				</button>
				{showHelp && (
					<div className={styles.helpTooltip}>
						<div className={styles.helpContent}>
							<h4>Quick Edit Mode</h4>
							<p>• Click "Quick Edit" to enter rapid grading mode</p>
							<p>• Press Enter or ↓ to save and move to next student</p>
							<p>• Use ↑ arrow key to save and move to previous student</p>
							<p>• Press Esc to exit quick edit mode</p>
							<br />
							<h4>Batch Operations</h4>
							<p>• Select students with checkboxes</p>
							<p>• Use "Full Points", "Zero", or "Custom" buttons</p>
						</div>
					</div>
				)}
			</div>
			<div className={styles.footerButtons}>
				<button className="btn btn-secondary" onClick={onClose}>
					Cancel
				</button>
				<button className="btn btn-primary" onClick={onClose}>
					Done
				</button>
			</div>
		</>
	);

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			title="" // We'll use custom header
			size="large"
			footer={footer}
			loading={isDataLoading}
			showCloseButton={false} // We'll add close button in custom header
			className={styles.assessmentGradesModal}
			ref={modalRef}
		>
			{customHeader}

			{/* Top Section */}
			<div className={styles.batchActions}>
				<div className={styles.searchContainer}>
					<FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
					<input
						type="text"
						className={styles.searchInput}
						placeholder="Search students..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
					/>
				</div>

				<div className={styles.filterActions}>
					<div className={styles.filterDropdown}>
						<button
							className={styles.filterButton}
							onClick={() => setShowFilterDropdown(!showFilterDropdown)}
						>
							<FontAwesomeIcon icon={faFilter} />
							<span>
								{filterMode === "all"
									? "All"
									: filterMode === "graded"
									? "Graded"
									: "Ungraded"}
							</span>
						</button>
						{showFilterDropdown && (
							<div className={styles.filterMenu}>
								<button
									className={`${styles.filterOption} ${
										filterMode === "all" ? styles.active : ""
									}`}
									onClick={() => {
										setFilterMode("all");
										setShowFilterDropdown(false);
									}}
								>
									All Students
								</button>
								<button
									className={`${styles.filterOption} ${
										filterMode === "graded" ? styles.active : ""
									}`}
									onClick={() => {
										setFilterMode("graded");
										setShowFilterDropdown(false);
									}}
								>
									Graded
								</button>
								<button
									className={`${styles.filterOption} ${
										filterMode === "ungraded" ? styles.active : ""
									}`}
									onClick={() => {
										setFilterMode("ungraded");
										setShowFilterDropdown(false);
									}}
								>
									Ungraded
								</button>
							</div>
						)}
					</div>

					<button
						className={`${styles.sortButton} ${
							sortConfig.key === "studentName" ? styles.active : ""
						}`}
						onClick={() => requestSort("studentName")}
						title="Sort by name"
					>
						<FontAwesomeIcon
							icon={
								sortConfig.key === "studentName" &&
								sortConfig.direction === "desc"
									? faSortAmountDown
									: faSortAmountUp
							}
						/>
						<span>Name</span>
					</button>

					<button
						className={`${styles.sortButton} ${
							sortConfig.key === "grade" ? styles.active : ""
						}`}
						onClick={() => requestSort("grade")}
						title="Sort by grade"
					>
						<FontAwesomeIcon
							icon={
								sortConfig.key === "grade" && sortConfig.direction === "desc"
									? faSortAmountDown
									: faSortAmountUp
							}
						/>
						<span>Grade</span>
					</button>
				</div>
			</div>

			{/* Batch Actions */}
			<div className={styles.batchActions}>
				<div className={styles.batchSelection}>
					<label className={styles.checkboxContainer}>
						<input
							type="checkbox"
							checked={
								selectedRows.length === visibleStudents.length &&
								visibleStudents.length > 0
							}
							onChange={toggleSelectAll}
						/>
						<span className={styles.checkboxCheckmark}></span>
						<span className={styles.checkboxLabel}>
							{selectedRows.length > 0
								? `${selectedRows.length} selected`
								: "Select All"}
						</span>
					</label>
				</div>

				<div className={styles.actionButtons}>
					<button
						className={styles.actionButton}
						disabled={selectedRows.length === 0}
						onClick={() => applyQuickGrade(assessment.points.toString())}
						title="Perfect score"
					>
						<FontAwesomeIcon icon={faCheckCircle} />
						<span>Full Points</span>
					</button>

					<button
						className={styles.actionButton}
						disabled={selectedRows.length === 0}
						onClick={() => applyQuickGrade("0")}
						title="Zero points"
					>
						<FontAwesomeIcon icon={faExclamationCircle} />
						<span>Zero</span>
					</button>

					<div className={styles.customGrade}>
						<input
							type="text"
							className={styles.customGradeInput}
							placeholder="Custom grade..."
							value={customGradeValue}
							onChange={(e) => {
								const validGradeRegex = /^(\d*\.?\d*)$/;
								if (
									e.target.value === "" ||
									validGradeRegex.test(e.target.value)
								) {
									setCustomGradeValue(e.target.value);
								}
							}}
							disabled={selectedRows.length === 0}
						/>
						<button
							className={styles.customGradeButton}
							disabled={selectedRows.length === 0 || customGradeValue === ""}
							onClick={() => {
								saveSelectedGrades(customGradeValue);
								setCustomGradeValue("");
							}}
							title="Apply custom grade"
						>
							Apply
						</button>
					</div>

					<button
						className={`${styles.quickEditButton} ${
							quickEditMode ? styles.active : ""
						}`}
						onClick={quickEditMode ? exitQuickEditMode : enterQuickEditMode}
						title="Quick edit mode"
					>
						<FontAwesomeIcon icon={faKeyboard} />
						<span>{quickEditMode ? "Exit" : "Quick Edit"}</span>
					</button>
				</div>
			</div>

			{/* Students Container */}
			<div className={styles.studentsContainer}>
				{isDataReady ? (
					visibleStudents.length > 0 ? (
						visibleStudents.map((student) => {
							const isEditing = editingRow === student.studentId;
							const isUngraded = !gradedStudentIds.has(student.studentId);
							const gradeValue = gradeValues[student.studentId] || "";
							const isSelected = selectedRows.includes(student.studentId);
							const isSavingThis = savingGrades.has(student.studentId);
							const hasError = saveErrors.has(student.studentId);

							return (
								<div
									key={student.studentId}
									className={`${styles.studentRow} ${
										isEditing ? styles.editing : ""
									} ${isSelected ? styles.selected : ""} ${
										hasError ? styles.error : ""
									}`}
									ref={(el) => (studentRowsRef.current[student.studentId] = el)}
								>
									<div className={styles.checkboxColumn}>
										<input
											type="checkbox"
											checked={isSelected}
											onChange={() => {
												if (isSelected) {
													setSelectedRows((prev) =>
														prev.filter((id) => id !== student.studentId)
													);
												} else {
													setSelectedRows((prev) => [
														...prev,
														student.studentId,
													]);
												}
											}}
										/>
									</div>

									<div className={styles.studentName}>
										{student.studentName}
									</div>

									<div className={styles.studentGrade}>
										{isEditing ? (
											<input
												type="text"
												className={styles.gradeInput}
												value={gradeValue}
												onChange={(e) =>
													handleGradeChange(student.studentId, e.target.value)
												}
												onKeyDown={(e) => handleKeyDown(e, student.studentId)}
												ref={(el) =>
													(inputRefs.current[student.studentId] = el)
												}
												autoFocus
												placeholder="Enter grade"
											/>
										) : (
											<span
												className={`${styles.gradeDisplay} ${
													isUngraded ? styles.notGraded : ""
												}`}
												onClick={() => {
													if (quickEditMode) {
														const visibleStudents = getVisibleStudents();
														const clickedIndex = visibleStudents.findIndex(
															(s) => s.studentId === student.studentId
														);
														setCurrentQuickEditIndex(clickedIndex);
														setEditingRow(student.studentId);

														// Track original value when starting to edit
														setOriginalGradeValues((prev) => ({
															...prev,
															[student.studentId]:
																gradeValues[student.studentId] || "",
														}));

														// Focus the input
														setTimeout(() => {
															const input =
																inputRefs.current[student.studentId];
															if (input && quickEditMode) {
																input.focus();
																input.select();
															}
														}, 50);
													}
												}}
												ref={(el) =>
													(gradeDisplaysRef.current[student.studentId] = el)
												}
											>
												{isUngraded ? "Not Graded" : gradeValue}
											</span>
										)}
									</div>

									<div className={styles.studentActions}>
										{isEditing ? (
											<button
												onClick={() => saveGrade(student.studentId)}
												className="btn btn-sm btn-success"
												title="Save grade"
												disabled={isSavingThis}
											>
												{isSavingThis ? (
													<FontAwesomeIcon icon={faSpinner} spin />
												) : (
													<FontAwesomeIcon icon={faSave} />
												)}
												<span>Save</span>
											</button>
										) : (
											<button
												onClick={() => {
													setEditingRow(student.studentId);
													// Track original value when starting to edit
													setOriginalGradeValues((prev) => ({
														...prev,
														[student.studentId]:
															gradeValues[student.studentId] || "",
													}));
												}}
												className="btn btn-sm btn-primary"
												title="Edit grade"
												disabled={isSavingThis}
											>
												{isSavingThis ? (
													<FontAwesomeIcon icon={faSpinner} spin />
												) : (
													<FontAwesomeIcon icon={faPen} />
												)}
												<span>Edit</span>
											</button>
										)}
									</div>
								</div>
							);
						})
					) : (
						<div className={styles.noResults}>
							<p>No students found matching your search criteria.</p>
						</div>
					)
				) : hasError ? (
					<div className={styles.error}>
						<FontAwesomeIcon icon={faExclamationCircle} />
						Error loading data. Please try again.
					</div>
				) : (
					<div className={styles.noResults}>
						<p>Loading students and grades...</p>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default AssessmentGradesModal;
